import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../pages/Auth/types';
import { API_ENDPOINTS } from '../pages/Auth/constants';
import ssoService from '../pages/Auth/ssoService';
import axios from 'axios';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (loading: boolean) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: true,

      setUser: (user) => set({ user }),
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setLoading: (loading) => set({ loading }),

      login: (user, accessToken, refreshToken) => {
        ssoService.storeTokens(accessToken, refreshToken);
        set({ user, isAuthenticated: true, loading: false });
      },

      logout: () => {
        ssoService.clearTokens();
        set({ user: null, isAuthenticated: false, loading: false });
      },

      checkAuth: async () => {
        const token = ssoService.getAccessToken();

        if (!token) {
          set({ loading: false, isAuthenticated: false, user: null });
          return;
        }

        if (ssoService.isTokenExpired(token)) {
          try {
            await ssoService.refreshAccessToken();
          } catch {
            get().logout();
            return;
          }
        }

        try {
          const currentToken = ssoService.getAccessToken();
          const response = await axios.get<User>(
            `${API_BASE_URL}${API_ENDPOINTS.CURRENT_USER}`,
            {
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            }
          );

          set({ user: response.data, isAuthenticated: true, loading: false });
        } catch {
          try {
            await ssoService.refreshAccessToken();
            const newToken = ssoService.getAccessToken();

            const response = await axios.get<User>(
              `${API_BASE_URL}${API_ENDPOINTS.CURRENT_USER}`,
              {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              }
            );

            set({ user: response.data, isAuthenticated: true, loading: false });
          } catch {
            get().logout();
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

